import { Rocket, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDay } from "@/lib/campaignPlanning";
import { STEP_COPY, TIERS, displayStatus } from "@/components/schools/setup/steps";
import { ActionButton, GuideTip, StepNotice, StepStatusIcon } from "@/components/schools/setup/SetupPrimitives";
import { quoteSteps } from "@/components/schools/setup/StepPanel";

const STATUS_LABELS = { done: "Done", skipped: "Skipped", in_progress: "Started", todo: "Not yet", blocked: "Not yet" };

/**
 * The finish line: a pre-flight check of every step, what happens on the
 * start date, and the link families will share. Campaigns launch by
 * themselves on the start date, so there's no button to press.
 */
export function LaunchStep({ step, steps, setup, onSelect }) {
  const { launch, key_dates: dates, campaign, notifications } = setup;
  if (!launch) return null;
  // What we promise follows the school's notification settings.
  const sends = (email) => notifications.enabled.includes(email);

  const tasks = steps.filter((s) => s.key !== "launch");
  const requiredLeft = tasks.filter((s) => s.tier === "required" && s.status !== "done");
  const recommendedLeft = tasks.filter((s) => s.tier === "recommended" && s.status !== "done" && s.status !== "skipped");

  // A signup draft's dates are placeholders: nothing to say about launch
  // day until the school sets them.
  if (!campaign.dates_confirmed) {
    return (
      <StepNotice tone="warning">
        {campaign.name} can't go live until you've finished {quoteSteps(requiredLeft.map((s) => s.key))}, starting with its dates.
      </StepNotice>
    );
  }

  return (
    <div className="space-y-6">
      {launch.running_campaign_name ? (
        <StepNotice tone="info">
          {launch.running_campaign_name} is still running. {campaign.name} goes live on {formatDay(dates.start_on)}, after it has finished.
        </StepNotice>
      ) : requiredLeft.length > 0 ? (
        <StepNotice tone="warning">
          {campaign.name} can't go live until you've finished {quoteSteps(requiredLeft.map((s) => s.key))}. If {requiredLeft.length === 1 ? "it's" : "they're"} not done by{" "}
          {formatDay(dates.start_on)}, it waits in setup until they are.
        </StepNotice>
      ) : (
        <div className="rounded-xl border border-success/25 bg-success/5 p-6">
          <div className="flex items-start gap-4">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-success text-success-foreground">
              <Rocket className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-xl font-semibold text-foreground">Ready for launch</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {campaign.name} goes live by itself early on {formatDay(dates.start_on)}. There's nothing more to press.
                {recommendedLeft.length > 0 && ` You've still got ${recommendedLeft.length} recommended step${recommendedLeft.length === 1 ? "" : "s"} to look at before then.`}
              </p>
            </div>
          </div>
        </div>
      )}

      <section>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Pre-flight check</h3>
        <ul className="divide-y divide-border rounded-xl border border-border">
          {tasks.map((task, i) => {
            const complete = task.status === "done" || task.status === "skipped";
            return (
              <li key={task.key} className="flex h-11 items-center gap-3 px-4">
                <StepStatusIcon status={displayStatus(task)} number={i + 1} size="sm" />
                <span className={cn("flex-1 text-sm", complete ? "text-muted-foreground" : "font-medium text-foreground")}>
                  {STEP_COPY[task.key].title}
                </span>
                {!complete && (
                  <span className={cn("hidden text-xs sm:inline", task.tier === "required" ? "text-primary" : "text-muted-foreground")}>
                    {TIERS[task.tier].label}
                  </span>
                )}
                {complete ? (
                  <span className="text-xs text-muted-foreground">{STATUS_LABELS[task.status]}</span>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7 px-3" onClick={() => onSelect(task.key)}>Go</Button>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2 [&>*]:min-w-0">
        <div className="rounded-xl border border-border p-5 text-sm">
          <h3 className="font-semibold text-foreground">What happens on {formatDay(dates.start_on)}</h3>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-muted-foreground">
            {sends("teacher_reading_starts") ? (
              <li>Teachers get a “Reading starts today” email with a link to their class.</li>
            ) : (
              <li>Teachers can start logging class reading. You've turned off the “Reading starts today” email, so let them know it's begun.</li>
            )}
            <li>Families and teachers can log reading minutes until {dates.minutes_close_at}.</li>
            {sends("admin_daily_digest") ? (
              <li>Your daily summary adds reading minutes to the donations it already reports.</li>
            ) : (
              <li>Your dashboard shows new minutes and donations as they come in.</li>
            )}
            <li>Donations stay open until {formatDay(dates.donations_close_on)}.</li>
          </ul>
        </div>
        <div className="rounded-xl border border-border p-5 text-sm">
          <h3 className="font-semibold text-foreground">Your school's page</h3>
          <p className="mt-1 text-muted-foreground">
            {launch.public_page_open
              ? "It's live now, taking donations for the whole school. Share it in your newsletter alongside each child's page."
              : dates.donations_open_any_time
                ? "It opens when donations do, along with each child's page. Copy the link now for your newsletter."
                : `It opens when donations do on ${formatDay(dates.donations_open_on)}, along with each child's page. Copy the link now for your newsletter.`}
          </p>
          <p className="mt-3 truncate rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-foreground">{launch.public_url}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <ActionButton action={{ label: "Copy link", copyText: launch.public_url, icon: Copy }} />
          </div>
        </div>
      </section>

      <GuideTip title="Plan two big sharing moments" href="/help/articles/sharing-with-family" linkLabel="Sharing tips for families">
        Donations tend to come in two waves: right after families first share, and in the last few days. Remind families when donations open, and again near the end.
      </GuideTip>
    </div>
  );
}
